'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Document, DocumentType, PERMISSIONS } from '@repo/shared/domain';
import { getVerifyPermission } from '@repo/shared/helpers';
import DocumentTab from 'src/parts/tabs/DocumentTab';
import { getUserDocuments } from 'src/api/admin/documents';
import { getDocumentTypes } from 'src/api/admin/documentTypes';
import { useAuthenticatedUser } from 'src/hooks/useAuthenticatedUser';

export const Route = createFileRoute('/_admin/users/$userId/documents/')({
	validateSearch: (search: Record<string, unknown>) => {
		return {
			filter: (search.filter as string) || undefined,
			page: (search.page as number) || undefined,
		};
	},
	loaderDeps: ({ search: { filter, page } }) => ({ filter, page }),
	loader: async ({ params, deps }) => {
		const { userId } = params;
		const [documents, documentTypes] = await Promise.all([
			getUserDocuments(userId, { filter: deps.filter, page: deps.page }),
			getDocumentTypes('users'),
		]);
		return { documents, documentTypes };
	},
	component: DocumentsTab,
});

function DocumentsTab() {
	const { userId } = Route.useParams();
	const navigate = useNavigate();
	const queryClient = useQueryClient();
	const searchParams = Route.useSearch();
	const { documents: initialDocuments, documentTypes: initialDocumentTypes } = Route.useLoaderData();
	const { user: authenticatedUser, loading: authLoading } = useAuthenticatedUser();

	const { data: documents, refetch: refetchDocuments } = useQuery({
		queryKey: ['user-documents', userId, searchParams],
		queryFn: () => getUserDocuments(userId, { filter: searchParams.filter, page: searchParams.page }),
		initialData: initialDocuments,
	});

	const { data: documentTypes } = useQuery({
		queryKey: ['document-types', 'users'],
		queryFn: () => getDocumentTypes('users'),
		initialData: initialDocumentTypes,
	});

	const verifyPermission = getVerifyPermission(authenticatedUser?.permissions || []);

	const handleReload = () => {
		refetchDocuments();
		queryClient.invalidateQueries({ queryKey: ['user-profile', userId] });
	};

	return (
		<DocumentTab
			data={documents}
			documentTypes={documentTypes?.data || []}
			disableSorting
			revalidateDetail={handleReload}
			onReload={handleReload}
			permissions={{
				metadata: verifyPermission([PERMISSIONS.adminUserdocumentmetadataView]),
				fullView: verifyPermission([PERMISSIONS.adminUserdocumentsView]),
				upload: verifyPermission([PERMISSIONS.adminUserdocumentsCreate]),
				archive: verifyPermission([PERMISSIONS.adminUserdocumentsArchive]),
				edit: verifyPermission([PERMISSIONS.adminUserdocumentsEdit]),
			}}
			apiUrl={`/api/users/${userId}/files`}
			onSelectDocument={(id: string) =>
				navigate({ to: `/users/$userId/documents/$documentId`, params: { userId, documentId: id } })
			}
		/>
	);
}
